import { useState, useEffect, useRef, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { X, Send, Sparkles, Loader2, ArrowRight } from 'lucide-react';
import ReactMarkdown from 'react-markdown';
import { useAssistantChat } from './useAssistantChat';

interface CommandPaletteProps {
  isOpen: boolean;
  onClose: () => void;
}

export function CommandPalette({ isOpen, onClose }: CommandPaletteProps) {
  const navigate = useNavigate();
  const [input, setInput] = useState('');
  const inputRef = useRef<HTMLInputElement>(null);
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const {
    messages,
    isLoading,
    error,
    sendMessage,
    executeAction,
    clearConversation,
  } = useAssistantChat();

  // Lock body scroll when open
  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = 'unset';
    }
    return () => {
      document.body.style.overflow = 'unset';
    };
  }, [isOpen]);

  // Focus input when opened
  useEffect(() => {
    if (isOpen) {
      setTimeout(() => inputRef.current?.focus(), 100);
    }
  }, [isOpen]);

  // Scroll to bottom on new messages
  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  // Handle escape key
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && isOpen) {
        onClose();
      }
    };
    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [isOpen, onClose]);

  const handleSubmit = useCallback(
    async (e: React.FormEvent) => {
      e.preventDefault();
      if (!input.trim() || isLoading) return;

      const message = input;
      setInput('');
      await sendMessage(message);
    },
    [input, isLoading, sendMessage]
  );

  const handleNavigate = useCallback(
    (route: string) => {
      navigate(route);
      onClose();
    },
    [navigate, onClose]
  );

  const handleClose = useCallback(() => {
    onClose();
    // Clear conversation after a delay so it doesn't flash
    setTimeout(clearConversation, 300);
  }, [onClose, clearConversation]);

  const handleExecuteAction = useCallback(
    async (actionId: string) => {
      await executeAction(actionId);
    },
    [executeAction]
  );

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-start justify-center pt-[10vh] sm:pt-[15vh] p-3 sm:p-4">
      {/* Backdrop */}
      <div
        className="fixed inset-0 bg-black/50 backdrop-blur-sm"
        onClick={handleClose}
      />

      {/* Modal */}
      <div className="relative w-full max-w-2xl bg-surface border border-border rounded-xl shadow-xl overflow-hidden animate-in fade-in zoom-in-95 duration-200">
        {/* Header */}
        <div className="flex items-center justify-between px-4 py-3 border-b border-border">
          <div className="flex items-center gap-2 text-text-secondary">
            <Sparkles className="w-5 h-5 text-primary" />
            <span className="font-medium text-text">AI Assistant</span>
            <span className="hidden sm:inline-block text-xs text-text-secondary bg-surface-secondary px-2 py-0.5 rounded">
              Cmd+K
            </span>
          </div>
          <button
            onClick={handleClose}
            className="p-1 text-text-secondary hover:text-text hover:bg-surface-hover rounded transition-colors"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Messages */}
        <div className="max-h-[50vh] overflow-y-auto p-4 space-y-4">
          {messages.length === 0 && (
            <div className="text-center text-text-secondary py-8">
              <Sparkles className="w-8 h-8 mx-auto mb-2 text-primary/60" />
              <p className="font-medium text-text">How can I help you?</p>
              <p className="text-sm mt-1">
                Ask about customers, invoices, navigation, or troubleshooting
              </p>
            </div>
          )}

          {messages.map((message, index) => (
            <div
              key={index}
              className={`flex ${
                message.role === 'user' ? 'justify-end' : 'justify-start'
              }`}
            >
              <div
                className={`max-w-[85%] rounded-lg px-4 py-2 ${
                  message.role === 'user'
                    ? 'bg-primary text-primary-foreground'
                    : 'bg-surface-secondary text-text'
                }`}
              >
                {message.role === 'assistant' ? (
                  <div className="prose prose-sm dark:prose-invert max-w-none [&>p]:mb-2 [&>p:last-child]:mb-0">
                    <ReactMarkdown>{message.content}</ReactMarkdown>
                  </div>
                ) : (
                  <p>{message.content}</p>
                )}

                {/* Suggested Action */}
                {message.suggestedAction && (
                  <div className="mt-3 p-3 bg-surface rounded-lg border border-border">
                    <p className="font-medium text-sm text-text">
                      {message.suggestedAction.label}
                    </p>
                    <p className="text-xs text-text-secondary mt-1">
                      {message.suggestedAction.description}
                    </p>
                    <div className="flex gap-2 mt-2">
                      <button
                        onClick={() =>
                          handleExecuteAction(message.suggestedAction!.actionId)
                        }
                        disabled={isLoading}
                        className="px-3 py-1.5 text-sm font-medium bg-primary text-primary-foreground rounded-lg hover:bg-primary-dark disabled:opacity-50 transition-colors"
                      >
                        Confirm
                      </button>
                      <button className="px-3 py-1.5 text-sm font-medium text-text-secondary hover:bg-surface-hover rounded-lg transition-colors">
                        Cancel
                      </button>
                    </div>
                  </div>
                )}

                {/* Related Pages */}
                {message.relatedPages && message.relatedPages.length > 0 && (
                  <div className="mt-3 flex flex-wrap gap-2">
                    {message.relatedPages.map((page, idx) => (
                      <button
                        key={idx}
                        onClick={() => handleNavigate(page.route)}
                        className="inline-flex items-center gap-1 px-2 py-1 text-xs font-medium bg-primary/10 text-primary rounded hover:bg-primary/20 transition-colors"
                      >
                        {page.label}
                        <ArrowRight className="w-3 h-3" />
                      </button>
                    ))}
                  </div>
                )}
              </div>
            </div>
          ))}

          {isLoading && (
            <div className="flex justify-start">
              <div className="bg-surface-secondary rounded-lg px-4 py-3">
                <Loader2 className="w-5 h-5 animate-spin text-text-secondary" />
              </div>
            </div>
          )}

          {error && (
            <div className="text-center text-error text-sm py-2 px-4 bg-error/10 rounded-lg">
              {error}
            </div>
          )}

          <div ref={messagesEndRef} />
        </div>

        {/* Input */}
        <form
          onSubmit={handleSubmit}
          className="border-t border-border p-4"
        >
          <div className="flex gap-2">
            <input
              ref={inputRef}
              type="text"
              value={input}
              onChange={(e) => setInput(e.target.value)}
              placeholder="Ask anything..."
              disabled={isLoading}
              className="flex-1 px-4 py-2.5 bg-surface-secondary border-0 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary text-text placeholder-text-secondary disabled:opacity-50"
            />
            <button
              type="submit"
              disabled={!input.trim() || isLoading}
              className="px-4 py-2.5 bg-primary text-primary-foreground rounded-lg hover:bg-primary-dark disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
            >
              <Send className="w-5 h-5" />
            </button>
          </div>
          <p className="text-xs text-text-secondary mt-2 text-center">
            Press{' '}
            <kbd className="px-1.5 py-0.5 bg-surface-secondary border border-border rounded text-xs">
              Esc
            </kbd>{' '}
            to close
          </p>
        </form>
      </div>
    </div>
  );
}
